import { useCallback, useEffect, useMemo } from 'react';
import { TableCell, TableRow, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { getIn, useFormik } from 'formik';

import { FormTextField, useFormReadOnly } from '../../../../../common';
import { ASSESSMENT_READINESS_OPTIONS } from '../constants';
import { useSaveAssessmentReadinessItem } from '../hooks';
import {
  ASSESSMENT_READINESS_TOGGLE_GROUP_SX,
  ASSESSMENT_READINESS_TOGGLE_SX,
  ASSESSMENT_READINESS_VALUE_CELL_SX,
} from '../styles';
import { ASSESSMENT_READINESS_NOTES_FORM_SCHEMA } from '../validationSchema';

import type { MouseEvent } from 'react';
import type { AssessmentReadinessValue } from '../../../../../../store/treatmentPlanV3';
import type { AssessmentReadinessRowProps } from './types';

export default function AssessmentReadinessRow({
  itemId,
  label,
  readiness,
  notes,
  disabled,
  onUpdateItem,
}: AssessmentReadinessRowProps) {
  const readOnly = useFormReadOnly();
  const { isSaving, saveItem } = useSaveAssessmentReadinessItem({ itemId, onUpdateItem });
  const initialValues = useMemo(() => ({ notes }), [notes]);
  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validateOnBlur: true,
    validateOnChange: true,
    validateOnMount: true,
    validationSchema: ASSESSMENT_READINESS_NOTES_FORM_SCHEMA,
    onSubmit: () => undefined,
  });

  const { setFieldTouched } = formik;
  useEffect(() => {
    if (readiness == null) return;
    void setFieldTouched('notes', true);
  }, [readiness, setFieldTouched]);

  const handleReadinessChange = useCallback(
    (_event: MouseEvent<HTMLElement>, value: AssessmentReadinessValue | null) => {
      if (value == null || value === readiness) return;
      void setFieldTouched('notes', true);
      void saveItem({ readiness: value });
    },
    [readiness, saveItem, setFieldTouched],
  );

  const handleNotesBlur = useCallback(() => {
    const nextNotes = String(getIn(formik.values, 'notes') ?? '').trim();
    if (
      nextNotes === notes ||
      !ASSESSMENT_READINESS_NOTES_FORM_SCHEMA.isValidSync({ notes: nextNotes })
    ) {
      return;
    }
    void saveItem({ notes: nextNotes });
  }, [formik.values, notes, saveItem]);

  return (
    <TableRow>
      <TableCell>{label}</TableCell>
      <TableCell sx={ASSESSMENT_READINESS_VALUE_CELL_SX}>
        <ToggleButtonGroup
          disabled={disabled || readOnly || isSaving}
          exclusive
          onChange={handleReadinessChange}
          size="small"
          sx={ASSESSMENT_READINESS_TOGGLE_GROUP_SX}
          value={readiness}
        >
          {ASSESSMENT_READINESS_OPTIONS.map((option) => (
            <ToggleButton key={option} sx={ASSESSMENT_READINESS_TOGGLE_SX} value={option}>
              {option}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </TableCell>
      <TableCell>
        <FormTextField
          disabled={disabled || isSaving}
          formik={formik}
          label="Notes"
          name="notes"
          onBlur={handleNotesBlur}
          placeholder="Enter details"
          readOnly={readOnly}
          required
          showFloatingLabel={false}
        />
      </TableCell>
    </TableRow>
  );
}
